import React, { useState } from 'react';
import SettingsScreen from './verve-settings/screens/SettingsScreen';

/**
 * SettingsPage - Refactored by USER to render the premium, unified Verve Settings panel.
 */
export const SettingsPage = () => {
  return <SettingsScreen />;
};

const DEFAULT_FIELDS = [
  { id: 'f1', label: 'Kitchen Length (ft)', type: 'number', category: 'Dimensions', required: true },
  { id: 'f2', label: 'Kitchen Width (ft)', type: 'number', category: 'Dimensions', required: true },
  { id: 'f3', label: 'Ceiling Height (ft)', type: 'number', category: 'Dimensions', required: true },
  { id: 'f4', label: 'Wall Type', type: 'select', category: 'Structure', required: true, options: 'Brick,Concrete,Drywall,Other' },
  { id: 'f5', label: 'Cabinet Count', type: 'number', category: 'Requirements', required: false },
  { id: 'f6', label: 'Material Preference', type: 'select', category: 'Requirements', required: false, options: 'Plywood,MDF,PVC,Solid Wood' },
];

export const LegacySettingsPage = () => {
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [activeTab, setActiveTab] = useState('measurement');
  const [editField, setEditField] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newField, setNewField] = useState({ label: '', type: 'text', category: 'Dimensions', required: false });
  const [saved, setSaved] = useState(false);

  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const handleAdd = () => {
    if (!newField.label) return;
    setFields(prev => [...prev, { id: `f${Date.now()}`, label: newField.label, type: newField.type || 'text', category: newField.category || 'Dimensions', required: !!newField.required }]);
    setNewField({ label: '', type: 'text', category: 'Dimensions', required: false });
    setShowForm(false);
  };
  const removeField = (id) => setFields(prev => prev.filter(f => f.id !== id));
  const toggleRequired = (id) => setFields(prev => prev.map(f => f.id === id ? { ...f, required: !f.required } : f));

  const tabs = [{ key: 'measurement', label: '📏 Measurement Form' }, { key: 'general', label: '⚙️ General' }, { key: 'notifications', label: '🔔 Notifications' }];

  return (
    <div className="max-w-4xl space-y-5">
      <div className="card overflow-hidden">
        <div className="flex border-b border-gray-100">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-5 py-3.5 text-sm font-medium transition-colors ${activeTab === t.key ? 'text-blum-orange border-b-2 border-blum-orange bg-orange-50/50' : 'text-blum-slate hover:text-blum-dark'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'measurement' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display font-semibold text-blum-dark">Measurement Form Fields</h3>
                  <p className="text-xs text-blum-slate mt-0.5">Configure the fields used during site visits</p>
                </div>
                <button onClick={() => setShowForm(true)} className="btn-primary">+ Add Field</button>
              </div>

              {showForm && (
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-100 space-y-3">
                  <h4 className="font-semibold text-sm text-blum-dark">New Field</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="label">Field Label *</label><input className="input-field" value={newField.label} onChange={e => setNewField({ ...newField, label: e.target.value })} placeholder="e.g. Kitchen Length (ft)" /></div>
                    <div><label className="label">Field Type</label>
                      <select className="select-field" value={newField.type} onChange={e => setNewField({ ...newField, type: e.target.value })}>
                        {['text','number','select','checkbox'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                      </select>
                    </div>
                    <div><label className="label">Category</label>
                      <select className="select-field" value={newField.category} onChange={e => setNewField({ ...newField, category: e.target.value })}>
                        {['Dimensions','Structure','Requirements','Utilities','Additional'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-2 pt-5">
                      <input type="checkbox" id="req" checked={!!newField.required} onChange={e => setNewField({ ...newField, required: e.target.checked })} className="w-4 h-4 accent-blum-orange" />
                      <label htmlFor="req" className="text-sm text-blum-dark">Required field</label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                    <button className="btn-primary" onClick={handleAdd}>Add Field</button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {['Dimensions','Structure','Requirements','Utilities','Additional'].map(cat => {
                  const catFields = fields.filter(f => f.category === cat);
                  if (!catFields.length) return null;
                  return (
                    <div key={cat}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-1 h-4 bg-blum-orange rounded"></div>
                        <span className="text-xs font-semibold text-blum-slate uppercase tracking-wider">{cat}</span>
                      </div>
                      <div className="space-y-1.5 ml-3">
                        {catFields.map(f => (
                          <div key={f.id} className="flex items-center gap-3 bg-white border border-gray-100 rounded-lg px-4 py-2.5 hover:border-gray-200 transition-colors">
                            <div className="flex-1">
                              <span className="text-sm font-medium text-blum-dark">{f.label}</span>
                              <div className="flex gap-2 mt-0.5">
                                <span className="text-xs text-blum-slate">{f.type}</span>
                                {f.required && <span className="text-xs text-blum-orange font-medium">required</span>}
                              </div>
                            </div>
                            <div className="flex gap-2 items-center">
                              <button onClick={() => toggleRequired(f.id)} className={`text-xs px-2 py-1 rounded-lg transition-colors ${f.required ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                                {f.required ? 'Required' : 'Optional'}
                              </button>
                              <button onClick={() => removeField(f.id)} className="text-red-400 hover:text-red-600 transition-colors p-1 rounded">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'general' && (
            <div className="space-y-5">
              <h3 className="font-display font-semibold text-blum-dark">General Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Company Name</label><input className="input-field" defaultValue="Blum Furniture Solutions" /></div>
                <div><label className="label">Company Phone</label><input className="input-field" defaultValue="+91 40 1234 5678" /></div>
                <div><label className="label">Company Email</label><input className="input-field" defaultValue="info@blum.in" /></div>
                <div><label className="label">GST Number</label><input className="input-field" defaultValue="36AABCU9603R1ZT" /></div>
                <div className="col-span-2"><label className="label">Company Address</label><textarea className="input-field resize-none" rows={2} defaultValue="Plot 42, Industrial Area, Hyderabad - 500032, Telangana" /></div>
                <div><label className="label">Currency</label>
                  <select className="select-field"><option>INR (₹)</option><option>USD ($)</option></select>
                </div>
                <div><label className="label">Date Format</label>
                  <select className="select-field"><option>DD/MM/YYYY</option><option>MM/DD/YYYY</option></select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <h3 className="font-display font-semibold text-blum-dark">Notification Preferences</h3>
              {[
                { label: 'New Lead Notification', desc: 'Get notified when a new lead is added', enabled: true },
                { label: 'Low Stock Alert', desc: 'Alert when inventory drops below minimum', enabled: true },
                { label: 'Payment Reminders', desc: 'Remind for pending payments', enabled: true },
                { label: 'Project Deadline Alerts', desc: 'Notify before project deadlines', enabled: false },
                { label: 'Site Visit Reminders', desc: 'Remind about scheduled site visits', enabled: true },
                { label: 'Daily Summary Email', desc: 'Daily business summary email', enabled: false },
              ].map(({ label, desc, enabled }) => (
                <div key={label} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <div className="text-sm font-semibold text-blum-dark">{label}</div>
                    <div className="text-xs text-blum-slate mt-0.5">{desc}</div>
                  </div>
                  <div className={`w-10 h-5 rounded-full cursor-pointer transition-colors relative ${enabled ? 'bg-blum-orange' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${enabled ? 'left-5' : 'left-0.5'}`}></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} className={`btn-primary px-8 ${saved ? 'bg-green-600 hover:bg-green-700' : ''}`}>
          {saved ? '✓ Saved!' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};
